import type { NavigationType } from "react-router-dom";

import {
  listBreadcrumbParentOverrides,
  listNavBreadcrumbParentCandidates,
  staticBreadcrumbLabelForPath,
} from "./breadcrumbRouteLabels";
import {
  isChromeNavigationTrailPath,
  isModuleHomeNavigationPath,
  isNavigationTrailRootPath,
  normalizeNavigationTrailPathname,
} from "./navigationTrailRoots";

export { isNavigationTrailRootPath } from "./navigationTrailRoots";

export interface NavigationTrailEntry {
  pathname: string;
  search: string;
  label: string;
}

export type NavigationTrailPendingAction =
  | { type: "reset" }
  | { type: "trim"; index: number }
  | null;

/** Legacy full-trail JSON param (read-only compat). */
export const HUB_TRAIL_QUERY_PARAM = "hubTrail";

/** Durable one-hop parent pathname for paste/bookmark/refresh. */
export const FROM_QUERY_PARAM = "from";

/** Full in-session trail stored on each history entry. */
export const NAVIGATION_TRAIL_STATE_KEY = "hubNavigationTrail";

export function normalizeTrailPathname(pathname: string): string {
  return normalizeNavigationTrailPathname(pathname);
}

export function omitChromeNavigationTrailEntries(
  trail: readonly NavigationTrailEntry[],
): NavigationTrailEntry[] {
  return trail.filter(
    (entry) =>
      !isChromeNavigationTrailPath(entry.pathname)
      && !isModuleHomeNavigationPath(entry.pathname),
  );
}

export type NavigationTrailSyncResult = {
  trail: NavigationTrailEntry[];
  updateSearch: boolean;
  search?: string;
};

function parseSearchParams(search: string): URLSearchParams {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized);
}

function normalizeSearchString(search: string): string {
  if (search.length === 0) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

export function stripNavigationTrailFromSearch(search: string): string {
  const params = parseSearchParams(search);
  params.delete(HUB_TRAIL_QUERY_PARAM);
  params.delete(FROM_QUERY_PARAM);
  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : "";
}

/** @deprecated Use stripNavigationTrailFromSearch. */
export function stripHubTrailFromSearch(search: string): string {
  return stripNavigationTrailFromSearch(search);
}

export function navigationTrailLocationKey(pathname: string, search: string): string {
  return `${pathname}${normalizeSearchString(stripNavigationTrailFromSearch(search))}`;
}

/** Path + search stored on hub support tickets — strips trail params, keeps other query params. */
export function hubSupportTicketPagePath(pathname: string, search: string): string {
  return navigationTrailLocationKey(pathname, search);
}

export function hubSupportTicketPageUrlFull(input: {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}): string {
  return `${input.origin}${hubSupportTicketPagePath(input.pathname, input.search)}${input.hash}`;
}

function parseNavigationTrailJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return JSON.parse(decodeURIComponent(raw));
  }
}

function decodeNavigationTrailJson(raw: string): NavigationTrailEntry[] | null {
  try {
    const parsed: unknown = parseNavigationTrailJson(raw);
    if (!Array.isArray(parsed)) return null;

    const trail: NavigationTrailEntry[] = [];
    for (const item of parsed) {
      if (item == null || typeof item !== "object") return null;
      const entry = item as Record<string, unknown>;
      if (typeof entry.pathname !== "string" || typeof entry.search !== "string" || typeof entry.label !== "string") {
        return null;
      }
      trail.push({
        pathname: entry.pathname,
        search: stripNavigationTrailFromSearch(entry.search),
        label: entry.label,
      });
    }
    return trail;
  } catch {
    return null;
  }
}

function readLegacyTrailFromSearch(search: string): NavigationTrailEntry[] {
  const raw = parseSearchParams(search).get(HUB_TRAIL_QUERY_PARAM);
  if (raw == null || raw.trim() === "") return [];
  return omitChromeNavigationTrailEntries(decodeNavigationTrailJson(raw) ?? []);
}

function readOneHopFromSearchParam(
  search: string,
  registeredLabels: ReadonlyMap<string, string>,
): NavigationTrailEntry[] {
  const fromPathname = parseSearchParams(search).get(FROM_QUERY_PARAM)?.trim() ?? "";
  if (fromPathname === "") return [];
  return omitChromeNavigationTrailEntries([
    {
      pathname: fromPathname,
      search: "",
      label: resolveNavigationTrailLabel(fromPathname, "", registeredLabels),
    },
  ]);
}

/** Durable trail encoded in the URL (legacy JSON or one-hop from=). */
export function readTrailFromSearch(
  search: string,
  registeredLabels: ReadonlyMap<string, string> = new Map(),
): NavigationTrailEntry[] {
  const legacyTrail = readLegacyTrailFromSearch(search);
  if (legacyTrail.length > 0) {
    return legacyTrail;
  }
  return readOneHopFromSearchParam(search, registeredLabels);
}

function isNavigationTrailEntry(value: unknown): value is NavigationTrailEntry {
  if (value == null || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.pathname === "string"
    && typeof entry.search === "string"
    && typeof entry.label === "string"
  );
}

export function readTrailFromHistoryState(state: unknown): NavigationTrailEntry[] {
  if (state == null || typeof state !== "object") return [];
  const raw = (state as Record<string, unknown>)[NAVIGATION_TRAIL_STATE_KEY];
  if (!Array.isArray(raw)) return [];
  const trail: NavigationTrailEntry[] = [];
  for (const item of raw) {
    if (!isNavigationTrailEntry(item)) return [];
    trail.push({
      pathname: item.pathname,
      search: stripNavigationTrailFromSearch(item.search),
      label: item.label,
    });
  }
  return omitChromeNavigationTrailEntries(trail);
}

export function readTrailFromLocation(
  search: string,
  state: unknown,
  registeredLabels: ReadonlyMap<string, string>,
): NavigationTrailEntry[] {
  const stateTrail = readTrailFromHistoryState(state);
  if (stateTrail.length > 0) {
    return stateTrail;
  }
  return readTrailFromSearch(search, registeredLabels);
}

/** Writes only the immediate parent pathname to the URL. */
export function buildSearchWithTrail(baseSearch: string, trail: NavigationTrailEntry[]): string {
  const params = parseSearchParams(stripNavigationTrailFromSearch(baseSearch));
  const cleanTrail = omitChromeNavigationTrailEntries(trail);
  const immediateParent = cleanTrail[cleanTrail.length - 1];
  if (immediateParent != null) {
    params.set(FROM_QUERY_PARAM, immediateParent.pathname);
  }
  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : "";
}

export function buildNavigationTrailState(
  trail: NavigationTrailEntry[],
): Record<string, NavigationTrailEntry[]> | undefined {
  const clean = omitChromeNavigationTrailEntries(trail);
  if (clean.length === 0) return undefined;
  return { [NAVIGATION_TRAIL_STATE_KEY]: clean };
}

export function mergeNavigationTrailState(
  existingState: unknown,
  trail: NavigationTrailEntry[],
): Record<string, unknown> | null {
  const clean = omitChromeNavigationTrailEntries(trail);
  const base =
    existingState != null && typeof existingState === "object"
      ? { ...(existingState as Record<string, unknown>) }
      : {};
  if (clean.length === 0) {
    delete base[NAVIGATION_TRAIL_STATE_KEY];
    return Object.keys(base).length > 0 ? base : null;
  }
  base[NAVIGATION_TRAIL_STATE_KEY] = clean;
  return base;
}

export function buildNavigateTargetWithTrail(input: {
  pathname: string;
  baseSearch: string;
  trail: NavigationTrailEntry[];
  existingState?: unknown;
}): { pathname: string; search: string; state: Record<string, unknown> | null } {
  const search = buildSearchWithTrail(input.baseSearch, input.trail);
  return {
    pathname: input.pathname,
    search,
    state: mergeNavigationTrailState(input.existingState, input.trail),
  };
}

export function navigationTrailsEqual(
  left: NavigationTrailEntry[],
  right: NavigationTrailEntry[] | null,
): boolean {
  if (right == null) return left.length === 0;
  if (left.length !== right.length) return false;
  return left.every((entry, index) => {
    const other = right[index];
    return (
      other != null &&
      entry.pathname === other.pathname &&
      entry.search === other.search &&
      entry.label === other.label
    );
  });
}

export function resolveNavigationTrailLabel(
  pathname: string,
  search: string,
  registeredLabels: ReadonlyMap<string, string>,
): string {
  const cleanSearch = stripNavigationTrailFromSearch(search);
  const fullKey = navigationTrailLocationKey(pathname, cleanSearch);
  const registered = registeredLabels.get(fullKey) ?? registeredLabels.get(pathname);
  if (registered != null && registered.trim() !== "") return registered;
  return staticBreadcrumbLabelForPath(pathname);
}

/** Immediate list/overview parent for a detail or create route (null for list pages themselves). */
export function resolveImmediateParentListPathname(pathname: string): string | null {
  const inferred = inferNavigationTrailFromPathname(pathname);
  if (inferred.length === 0) {
    return null;
  }
  return inferred[inferred.length - 1]?.pathname ?? null;
}

function isHubListOverviewPath(pathname: string): boolean {
  const normalized = normalizeTrailPathname(pathname);
  if (isNavigationTrailRootPath(normalized)) {
    return true;
  }
  if (isChromeNavigationTrailPath(normalized) || isModuleHomeNavigationPath(normalized)) {
    return false;
  }
  return inferNavigationTrailFromPathname(normalized).length === 0;
}

/** Fixed create-flow segments — not record ids, so create→detail is never sibling navigation. */
const NON_RECORD_ROUTE_SEGMENTS = new Set(["new", "compose", "create"]);

function restContainsNonRecordRouteSegment(rest: string): boolean {
  return rest.split("/").some((segment) => NON_RECORD_ROUTE_SEGMENTS.has(segment));
}

/**
 * Another record on the same list replaces the current page.
 * The previous record is not a parent, whether the id is the only extra segment
 * (`/sales-order-details/1` to `/sales-order-details/2`) or one segment among a shared suffix
 * (`/accounting/ap/1/edit` to `/accounting/ap/2/edit`).
 * Create flows (`/new`, `/compose`) are not records — navigating from create to a saved
 * detail appends the create page instead of treating it as a sibling swap.
 */
export function isSiblingRecordNavigation(previousPathname: string, nextPathname: string): boolean {
  const previousParent = resolveImmediateParentListPathname(previousPathname);
  const nextParent = resolveImmediateParentListPathname(nextPathname);
  if (previousParent == null || previousParent !== nextParent) {
    return false;
  }
  const previousRest = normalizeTrailPathname(previousPathname).slice(previousParent.length + 1);
  const nextRest = normalizeTrailPathname(nextPathname).slice(nextParent.length + 1);
  if (previousRest === "" || nextRest === "" || previousRest === nextRest) {
    return false;
  }
  if (restContainsNonRecordRouteSegment(previousRest) || restContainsNonRecordRouteSegment(nextRest)) {
    return false;
  }
  const previousParts = previousRest.split("/");
  const nextParts = nextRest.split("/");
  if (previousParts.length !== nextParts.length) {
    return false;
  }
  let differences = 0;
  for (let index = 0; index < previousParts.length; index += 1) {
    if (previousParts[index] !== nextParts[index]) {
      differences += 1;
    }
  }
  return differences === 1;
}

function omitSiblingRecordsFromTrail(
  trail: NavigationTrailEntry[],
  nextPathname: string,
): NavigationTrailEntry[] {
  const nextKey = normalizeTrailPathname(nextPathname);
  return trail.filter((entry) => {
    const entryPath = normalizeTrailPathname(entry.pathname);
    if (entryPath === nextKey) {
      return false;
    }
    return !isSiblingRecordNavigation(entry.pathname, nextPathname);
  });
}

/** Forward navigation from a child route back to its list/overview ends the trail. */
export function isUpwardNavigationToListOverview(previousPathname: string, nextPathname: string): boolean {
  const normalizedPrevious = normalizeTrailPathname(previousPathname);
  const normalizedNext = normalizeTrailPathname(nextPathname);
  if (normalizedPrevious === normalizedNext) {
    return false;
  }

  const parentOfPrevious = resolveImmediateParentListPathname(normalizedPrevious);
  if (parentOfPrevious != null && normalizeTrailPathname(parentOfPrevious) === normalizedNext) {
    return true;
  }

  return (
    isHubListOverviewPath(normalizedNext)
    && normalizedPrevious.startsWith(`${normalizedNext}/`)
  );
}

/** When trail params are missing (refresh, replace navigation), infer the nearest nav parent. */
export function inferNavigationTrailFromPathname(pathname: string): NavigationTrailEntry[] {
  const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  for (const override of listBreadcrumbParentOverrides()) {
    if (!override.test.test(normalized)) {
      continue;
    }
    return omitChromeNavigationTrailEntries(
      override.parents.map((parent) => ({
        pathname: parent.pathname,
        search: "",
        label: parent.label,
      })),
    );
  }

  let best: { pathname: string; label: string } | null = null;
  for (const candidate of listNavBreadcrumbParentCandidates()) {
    if (normalized === candidate.pathname) {
      continue;
    }
    if (isModuleHomeNavigationPath(candidate.pathname)) {
      continue;
    }
    if (isNavigationTrailRootPath(candidate.pathname)) {
      continue;
    }
    if (!normalized.startsWith(`${candidate.pathname}/`)) {
      continue;
    }
    if (best == null || candidate.pathname.length > best.pathname.length) {
      best = candidate;
    }
  }
  if (best == null || isChromeNavigationTrailPath(best.pathname) || isNavigationTrailRootPath(best.pathname)) {
    return [];
  }
  return [{ pathname: best.pathname, search: "", label: best.label }];
}

function appendPreviousPageToTrail(
  previousTrail: NavigationTrailEntry[],
  previousPathname: string,
  previousSearch: string,
  _nextPathname: string,
  registeredLabels: ReadonlyMap<string, string>,
): NavigationTrailEntry[] {
  const cleanedPrevious = omitChromeNavigationTrailEntries(previousTrail);
  if (isChromeNavigationTrailPath(previousPathname) || isModuleHomeNavigationPath(previousPathname)) {
    return cleanedPrevious;
  }
  const previousCleanSearch = stripNavigationTrailFromSearch(previousSearch);
  const previousKey = navigationTrailLocationKey(previousPathname, previousCleanSearch);
  const last = cleanedPrevious[cleanedPrevious.length - 1];
  if (last != null && navigationTrailLocationKey(last.pathname, last.search) === previousKey) {
    return cleanedPrevious;
  }
  const existingIndex = cleanedPrevious.findIndex(
    (entry) => navigationTrailLocationKey(entry.pathname, entry.search) === previousKey,
  );
  if (existingIndex >= 0) {
    return cleanedPrevious.slice(0, existingIndex);
  }
  return [
    ...cleanedPrevious,
    {
      pathname: previousPathname,
      search: previousCleanSearch,
      label: resolveNavigationTrailLabel(previousPathname, previousCleanSearch, registeredLabels),
    },
  ];
}

function trailSyncResult(baseSearch: string, trail: NavigationTrailEntry[]): NavigationTrailSyncResult {
  const clean = omitChromeNavigationTrailEntries(trail);
  const builtSearch = buildSearchWithTrail(baseSearch, clean);
  return {
    trail: clean,
    updateSearch: normalizeSearchString(builtSearch) !== normalizeSearchString(baseSearch),
    search: builtSearch,
  };
}

function readEffectiveTrailFromNextLocation(input: {
  nextSearch: string;
  nextState: unknown;
  registeredLabels: ReadonlyMap<string, string>;
}): NavigationTrailEntry[] {
  const stateTrail = readTrailFromHistoryState(input.nextState);
  if (stateTrail.length > 0) {
    return stateTrail;
  }
  return readTrailFromSearch(input.nextSearch, input.registeredLabels);
}

export function syncNavigationTrailOnLocationChange(input: {
  previousPathname: string;
  previousSearch: string;
  previousState: unknown;
  nextPathname: string;
  nextSearch: string;
  nextState: unknown;
  navigationType: NavigationType;
  pendingAction: NavigationTrailPendingAction;
  registeredLabels: ReadonlyMap<string, string>;
}): NavigationTrailSyncResult {
  const {
    previousPathname,
    previousSearch,
    previousState,
    nextPathname,
    nextSearch,
    nextState,
    navigationType,
    pendingAction,
    registeredLabels,
  } = input;

  const nextLocationTrail = readEffectiveTrailFromNextLocation({
    nextSearch,
    nextState,
    registeredLabels,
  });
  const normalizedNextPath = normalizeTrailPathname(nextPathname);

  if (previousPathname === nextPathname) {
    const previousTrail = readTrailFromLocation(previousSearch, previousState, registeredLabels);
    const trail = nextLocationTrail.length > 0 ? nextLocationTrail : previousTrail;
    return trailSyncResult(nextSearch, trail);
  }

  if (pendingAction?.type === "reset" || isNavigationTrailRootPath(normalizedNextPath)) {
    const strippedSearch = stripNavigationTrailFromSearch(nextSearch);
    return {
      trail: [],
      updateSearch: normalizeSearchString(strippedSearch) !== normalizeSearchString(nextSearch),
      search: strippedSearch,
    };
  }

  if (pendingAction?.type === "trim") {
    return trailSyncResult(nextSearch, nextLocationTrail);
  }

  if (isUpwardNavigationToListOverview(previousPathname, nextPathname)) {
    return trailSyncResult(stripNavigationTrailFromSearch(nextSearch), []);
  }

  if (isSiblingRecordNavigation(previousPathname, nextPathname)) {
    const previousTrail = readTrailFromLocation(previousSearch, previousState, registeredLabels);
    return trailSyncResult(
      stripNavigationTrailFromSearch(nextSearch),
      omitSiblingRecordsFromTrail(previousTrail, nextPathname),
    );
  }

  if (navigationType === "POP") {
    return trailSyncResult(nextSearch, nextLocationTrail);
  }

  if (navigationType === "REPLACE") {
    if (nextLocationTrail.length > 0) {
      return trailSyncResult(nextSearch, nextLocationTrail);
    }
    if (previousPathname !== nextPathname) {
      return trailSyncResult(
        nextSearch,
        appendPreviousPageToTrail(
          readTrailFromLocation(previousSearch, previousState, registeredLabels),
          previousPathname,
          previousSearch,
          nextPathname,
          registeredLabels,
        ),
      );
    }
    return trailSyncResult(nextSearch, nextLocationTrail);
  }

  if (nextLocationTrail.length > 0) {
    return trailSyncResult(nextSearch, nextLocationTrail);
  }

  return trailSyncResult(
    nextSearch,
    appendPreviousPageToTrail(
      readTrailFromLocation(previousSearch, previousState, registeredLabels),
      previousPathname,
      previousSearch,
      nextPathname,
      registeredLabels,
    ),
  );
}
